import React, { useEffect, useRef } from "react";
import { View } from "../AdminDashboard";

type SidebarProps = {
  active: View;
  onChange: (v: View) => void;
  open?: boolean;
  onRequestClose?: () => void;
  onToggle?: () => void;
};

const items: { key: View; label: string; icon: React.ReactNode }[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    key: "invoices",
    label: "Invoices",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M21 8v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 2v6H8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    key: "flight_bookings",
    label: "Flight Bookings",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M2 16s4-1 6-3 6-5 10-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 16l4 2 2-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    key: "hotel_bookings",
    label: "Hotel Bookings",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="7" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M7 11h.01M11 11h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    key: "users",
    label: "Users",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M16 11c1.657 0 3-1.343 3-3S17.657 5 16 5s-3 1.343-3 3 1.343 3 3 3zM6 11c1.657 0 3-1.343 3-3S7.657 5 6 5 3 6.343 3 8s1.343 3 3 3z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 19c0-2.5 3-4 6-4s6 1.5 6 4M14 19c0-2.5 3-4 6-4s6 1.5 6 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  // 🔥 NEW: Discounts item
  {
    key: "discounts",
    label: "Discounts",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M20 12c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8z" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M15 9l-6 6M9 9h.01M15 15h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const Sidebar: React.FC<SidebarProps> = ({ active, onChange, open = true, onRequestClose, onToggle }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onRequestClose) onRequestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onRequestClose]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!open) return;
      if (!ref.current) return;
      if (window.innerWidth >= 992) return;
      const target = e.target as Node;
      if (!ref.current.contains(target) && onRequestClose) onRequestClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onRequestClose]);

  return (
    <>
      <div
        className={`sidebar-overlay ${open ? "visible" : ""}`}
        onClick={onRequestClose}
        aria-hidden={!open}
      />

      <aside
        ref={ref}
        className={`admin-sidebar d-flex flex-column ${open ? "open" : "collapsed"}`}
        role="navigation"
        aria-label="Admin sidebar"
      >
        <div className="sidebar-top p-3 d-flex align-items-center gap-2">
          <button className="btn-hamburger" onClick={onToggle} aria-label="Toggle sidebar">
            {open ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>

          <div className="brand d-flex align-items-center gap-2">
            
            {open && (
              <div>
                <div className="brand-title">Trio</div>
                <div className="small text-muted">Admin Panel</div>
              </div>
            )}
          </div>
        </div>

        <nav className="sidebar-nav mt-3 px-2 flex-grow-1">
          {items.map((it) => {
            const isActive = active === it.key;
            const itemClass = "sidebar-item d-flex align-items-center gap-3 py-2 px-3 rounded mb-2" + (isActive ? " active" : "");
            return (
              <button
                key={it.key}
                onClick={() => {
                  onChange(it.key);
                  if (window.innerWidth < 992 && onRequestClose) onRequestClose();
                }}
                className={itemClass}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="sidebar-icon">{it.icon}</span>
                {open && <span className="sidebar-label">{it.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-foot p-3">
          {open && <div className="small text-muted">Logged in as</div>}
          {open && <div className="fw-medium mt-1">admin@trio.com</div>}
          <div className="mt-3">
            <button className="btn btn-danger w-100 btn-sm">Sign out</button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;